import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentStarter } from '@/components/agent/AgentStarter';
import { ProviderSetup } from '@/components/provider/ProviderSetup';
import { Spinner } from '@/components/ui/button';
import { ctaCls } from '@/components/ui/link-styles';
import { useCan } from '@/lib/permissions';
import { qk, useOnboardingStatus, useProviders } from '@/lib/queries';
import { type SetupBlocker, type SetupNeed, setupBlocker } from '@/lib/setup-gate';
import { cn } from '@/lib/utils';

/**
 * Shows `children` only once what they depend on exists (provider → enabled model → agent);
 * otherwise the first missing step, solvable right here. Used wherever a new user would
 * otherwise hit an empty picker or a dead end.
 */
export function SetupGate({
  need,
  children,
  className,
}: {
  need: SetupNeed;
  children: ReactNode;
  className?: string;
}) {
  const status = useOnboardingStatus();
  const blocker = setupBlocker(status.data, need);
  if (status.isPending) return <Spinner />;
  if (!blocker) return <>{children}</>;
  return <SetupNeeded blocker={blocker} className={className} />;
}

export function SetupNeeded({ blocker, className }: { blocker: SetupBlocker; className?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canProvider = useCan('provider:create');
  const canModels = useCan('provider:update');
  const canAgent = useCan('agent:create');
  const providers = useProviders();
  const shell = cn('mx-auto flex w-full max-w-[760px] flex-col gap-4 px-4 py-6 sm:px-6', className);

  if (blocker === 'provider')
    return (
      <div className={shell}>
        <Intro
          title={t('setupGate.providerTitle')}
          why={canProvider ? t('setupGate.providerWhy') : t('setupGate.providerAsk')}
        />
        {canProvider ? (
          <div className="rounded-2xl border border-border bg-bg p-5 shadow-soft">
            <ProviderSetup
              providers={providers.data ?? []}
              onDone={() => void qc.invalidateQueries({ queryKey: qk.onboarding })}
            />
          </div>
        ) : null}
      </div>
    );

  if (blocker === 'models')
    return (
      <div className={shell}>
        <Intro
          title={t('setupGate.modelsTitle')}
          why={canModels ? t('setupGate.modelsWhy') : t('setupGate.modelsAsk')}
        />
        {canModels ? (
          <div>
            <Link to="/onboarding" search={{ step: 'models' }} className={ctaCls}>
              {t('setupGate.modelsAction')}
            </Link>
          </div>
        ) : null}
      </div>
    );

  return (
    <div className={shell}>
      <Intro
        title={t('setupGate.agentTitle')}
        why={canAgent ? t('setupGate.agentWhy') : t('setupGate.agentAsk')}
      />
      {canAgent ? (
        <AgentStarter
          onTemplate={(c) =>
            void navigate({ to: '/agents/new', search: c === 'blank' ? { blank: true } : { template: c.id } })
          }
          onDescribe={(describe) => void navigate({ to: '/agents/new', search: { describe } })}
        />
      ) : null}
    </div>
  );
}

function Intro({ title, why }: { title: string; why: string }) {
  return (
    <div>
      <h2 className="text-xl font-medium tracking-tight text-fg">{title}</h2>
      <p className="mt-1 text-sm text-muted">{why}</p>
    </div>
  );
}
